import {
    integrationClientFlagsMask,
    type IntegrationClientFlagName,
    type QueuedApiTaskModelType,
} from "constants/integrationClients";
import { externalPlatformsEnum, internalEntityTypesEnum } from "helpers/enums";
import { OdataFilters, queryCollection } from "helpers/reqparams";
import { alertRequest, body, request } from "services/request";

const resource = "api/adminPanel";

type ExternalPlatform = (typeof externalPlatformsEnum)[keyof typeof externalPlatformsEnum];

interface IntegrationClientSettings {
    allowedNetwork: string | null;
    flags: number;
    platform: ExternalPlatform | null;
    callbackTarget: string | null;
    callbackSecret: string | null;
}

export interface IntegrationClientListModel {
    clientId: string;
    name: string;
    allowedNetwork: string | null;
    flags: number | IntegrationClientFlagName;
    platform: ExternalPlatform | null;
    isActive: boolean;
    createdAt: string;
    lastUsedAt?: string | null;
}

export interface IntegrationClientEditModel extends IntegrationClientSettings {
    clientId: string;
    name: string;
    isActive: boolean;
}

export interface IntegrationClientApplyModel extends IntegrationClientEditModel {
    secret: string | null;
}

export interface IntegrationClientCreatedModel extends IntegrationClientEditModel {
    secret: string;
}

export interface IntegrationSubscriptionModel {
    entityType: (typeof internalEntityTypesEnum)[keyof typeof internalEntityTypesEnum];
    modelType: QueuedApiTaskModelType;
}

export interface OrganizationIntegrationClientListModel {
    organizationId: number;
    organizationName: string;
    clientId: string;
    technicalUserId: number;
    createdAt: string;
}

export const index = ({ preparedFilter, ...params }: OdataFilters) => {
    const query = queryCollection(params, { filter: preparedFilter, orderBy: "createdAt desc" });
    return request(`${resource}/getIntegrationClients${query}`, { method: "GET" }, { countInHeaders: true }).catch(
        () => false,
    );
};

export const getIntegrationClient = (clientId: string): Promise<IntegrationClientEditModel | false> =>
    request(`${resource}/getIntegrationClient/${encodeURIComponent(clientId)}`, { method: "GET" })
        .then((result) => {
            if (!result || typeof result !== "object") {
                return false as const;
            }

            return {
                ...result,
                flags: integrationClientFlagsMask(result.flags),
            };
        })
        .catch(() => false as const);

export const applyIntegrationClient = (
    model: IntegrationClientApplyModel,
): Promise<IntegrationClientCreatedModel | boolean> =>
    alertRequest(`${resource}/applyIntegrationClient`, body(model))
        .then((result) => result || false)
        .catch(() => false);

/** Quick actions always read the latest complete model, including callback credentials absent from the list. */
export const updateIntegrationClient = async (
    clientId: string,
    changes: { isActive?: boolean; secret?: string },
): Promise<IntegrationClientCreatedModel | boolean> => {
    const current = await getIntegrationClient(clientId);
    return current ? applyIntegrationClient({ ...current, secret: null, ...changes }) : false;
};

export const getIntegrationClientSubscriptions = (clientId: string): Promise<IntegrationSubscriptionModel[] | false> =>
    request(`${resource}/getIntegrationClientSubscriptions/${encodeURIComponent(clientId)}`, { method: "GET" })
        .then((result) => result || false)
        .catch(() => false);

export const setIntegrationClientSubscriptions = (
    clientId: string,
    subscriptions: IntegrationSubscriptionModel[],
): Promise<boolean> =>
    alertRequest(`${resource}/setIntegrationClientSubscriptions/${encodeURIComponent(clientId)}`, body(subscriptions))
        .then((result) => Boolean(result))
        .catch(() => false);

export const getOrganizationIntegrationClients = ({ preparedFilter, ...params }: OdataFilters) => {
    const query = queryCollection(params, { filter: preparedFilter, orderBy: "createdAt desc" });
    return request(
        `${resource}/getOrganizationIntegrationClients${query}`,
        { method: "GET" },
        { countInHeaders: true },
    ).catch(() => false);
};

export const applyOrganizationIntegrationClient = (model: { organizationId: number; clientId: string }) =>
    request(`${resource}/applyOrganizationIntegrationClient`, body(model)).catch(() => false);

export const deleteOrganizationIntegrationClient = (organizationId: number, clientId: string) =>
    request(`${resource}/deleteOrganizationIntegrationClient/${organizationId}/${encodeURIComponent(clientId)}`, {
        method: "POST",
    }).catch(() => false);
